<template>
  <div class="card reports-card">
    <div class="card-body">
      <div class="row justify-content-between bucket-header">
        <div class="col-auto d-flex align-items-center" style="gap: 12px">
          <h6 class="bucket-title">Bucket Lists</h6>
          <OwnerFilterPicker :model-value="bucketOwnerKey" :users="users" @select="onOwnerSelect" />
        </div>
        <div class="col-auto d-flex align-items-center">
          <Button
            @click="emit('add-bucket')"
            variant="secondary"
            size="sm"
            icon="mdi:plus"
            icon-position="left"
            class="new-bucket-btn"
          >
            New Bucket
          </Button>
        </div>
      </div>
      <div v-if="isLoading" class="bucket-loading-container">
        <LoadingBars />
      </div>
      <div v-if="!isLoading" class="bucket-legend">
        <div class="legend-item">
          <span class="legend-dot due-today-dot"></span>
          <span class="legend-text">Due Today</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot overdue-dot"></span>
          <span class="legend-text">Overdue</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot upcoming-dot"></span>
          <span class="legend-text">Upcoming</span>
        </div>
      </div>
      <div v-if="!isLoading" class="bucket-scroll-container" ref="bucketScroller">
        <div class="bucket-cards-row">
          <BucketListCard
            v-for="(bucketstat, index) in bucketStats"
            :key="index"
            :bucket-stat="bucketstat"
            :account="account"
            :is-locked="getBucketLockInfo(bucketstat.bucketOption?.slug).isLocked"
            :lock-reason="getBucketLockInfo(bucketstat.bucketOption?.slug).lockReason"
            :has-override="getBucketLockInfo(bucketstat.bucketOption?.slug).hasOverride"
            :unread-count="bucketstat.unreadCount || 0"
            @edit-bucket="(opt) => emit('edit-bucket', opt)"
            @delete-bucket="(opt) => emit('delete-bucket', opt)"
            ref="bucketCards"
          />
        </div>
      </div>
      <div
        class="bucket-pagination-wrapper"
        v-if="!isLoading && bucketStats?.length && bucketStats.length > 1"
      >
        <div class="bucket-pagination">
          <button
            class="bucket-nav-btn"
            type="button"
            aria-label="Previous"
            :disabled="!canPrev"
            @click="previousCard"
            title="Previous"
          >
            <Icon icon="mdi:chevron-left" width="20" height="20" />
          </button>
          <div class="bucket-dots-container">
            <div class="bucket-dots">
              <button
                v-for="index in visibleDots"
                :key="`dot-${index}`"
                class="bucket-dot"
                :class="{
                  active: index === currentCardIndex,
                  'dot-before-active': index === currentCardIndex - 1,
                  'dot-after-active': index === currentCardIndex + 1,
                }"
                @click="scrollToCard(index)"
                :title="`Go to bucket ${index + 1}`"
                :aria-label="`Go to bucket ${index + 1}`"
              ></button>
            </div>
            <span v-if="bucketStats.length > maxVisibleDots" class="dots-indicator">
              {{ currentCardIndex + 1 }} / {{ bucketStats.length }}
            </span>
          </div>
          <button
            class="bucket-nav-btn"
            type="button"
            aria-label="Next"
            :disabled="!canNext"
            @click="nextCard"
            title="Next"
          >
            <Icon icon="mdi:chevron-right" width="20" height="20" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { Button } from "@/components/ui";
import { Icon } from "@iconify/vue";
import OwnerFilterPicker from "@/components/common/OwnerFilterPicker.vue";
import BucketListCard from "./BucketListCard.vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import { useDashboardStats } from "@/composables/dashboard/useDashboardStats";
import { useUsers } from "@/composables/organization/useUsers";
import { usePermissions } from "@/composables/usePermissions";
import { useOrganizationSetting } from "@/composables/organization/useOrganizationSetting";
import { useBucketLockStatus } from "@/composables/bucket";

defineOptions({
  name: "BucketListSection",
});

const props = defineProps({
  account: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["add-bucket", "edit-bucket", "delete-bucket", "owner-selected"]);

const route = useRoute();
const store = useStore();

// Refs
const bucketScroller = ref(null);
const bucketCards = ref([]);
const currentCardIndex = ref(0);
const maxVisibleDots = ref(10);
const selectedOwner = ref(null);

// Account and organization
const account = computed(() => props.account);
const organizationId = computed(() => account.value?.organizationId);
const { canManageContacts } = usePermissions();

// Dashboard params for bucket stats
const dashboardParams = computed(() => {
  if (!organizationId.value) return null;

  const now = new Date();
  const dayOfWeek = now.getDay();
  const diffToMonday = (dayOfWeek + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - diffToMonday);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  const todayStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const todayEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  const params = {
    todayStartDate,
    todayEndDate,
    offset: new Date().getTimezoneOffset(),
    startDate: monday.toISOString(),
    endDate: sunday.toISOString(),
    organizationId: organizationId.value,
  };

  // Handle bucket owner filter
  if (canManageContacts.value) {
    if (route.query.bucketOwner && route.query.bucketOwner !== "All") {
      params.bucketOwnerId = route.query.bucketOwner;
    }
  } else {
    params.bucketOwnerId = account.value.id;
  }

  return params;
});

// Organization settings params
const orgSettingsParams = computed(() => {
  if (!organizationId.value) return null;
  return { organizationId: organizationId.value };
});

// TanStack Query hooks
const { data: dashboardStatsData, isLoading: isLoadingDashboardStats } =
  useDashboardStats(dashboardParams);

const { data: usersData } = useUsers(orgSettingsParams);
const { data: organizationSettingData } = useOrganizationSetting(orgSettingsParams);

// Bucket lock status
const bucketLockParams = computed(() => {
  if (!organizationId.value) return null;
  return {
    organizationId: organizationId.value,
    offset: new Date().getTimezoneOffset(),
  };
});

const { data: bucketLockStatusData } = useBucketLockStatus(bucketLockParams);

const getBucketLockInfo = (bucketOptionId) => {
  if (!bucketLockStatusData.value?.enabled) {
    return { isLocked: false, lockReason: "", hasOverride: false };
  }
  const status = bucketLockStatusData.value.bucketStatuses.find(
    (s) => s.bucketOptionId === bucketOptionId || s.bucketOptionSlug === bucketOptionId
  );
  if (!status) {
    return { isLocked: false, lockReason: "", hasOverride: false };
  }
  return {
    isLocked: status.isLocked || false,
    lockReason: status.lockReason || "",
    hasOverride: status.hasOverride || false,
  };
};

// Computed properties
const bucketStats = computed(() => {
  if (!dashboardStatsData.value?.bucketStatsByOption) return [];
  if (!organizationSettingData.value?.bucketOrder) {
    return dashboardStatsData.value.bucketStatsByOption;
  }

  const bucketOrder = organizationSettingData.value.bucketOrder;
  return [...dashboardStatsData.value.bucketStatsByOption].sort((a, b) => {
    return bucketOrder.indexOf(a.bucketOption.slug) - bucketOrder.indexOf(b.bucketOption.slug);
  });
});

const isLoading = computed(() => isLoadingDashboardStats.value);

const users = computed(() => usersData.value || []);

const bucketOwnerKey = computed(() => {
  const q = route.query.bucketOwner;
  if (!q || q === "All") return "all";
  return q;
});

const onOwnerSelect = (payload) => {
  emit("owner-selected", {
    user: payload === "all" ? "All" : payload,
    type: "bucket",
  });
};

const canPrev = computed(() => {
  return (currentCardIndex.value ?? 0) > 0;
});

const canNext = computed(() => {
  const total = bucketStats.value?.length || 0;
  return (currentCardIndex.value ?? 0) < Math.max(0, total - 1);
});

const visibleDots = computed(() => {
  const total = bucketStats.value?.length || 0;
  if (total <= maxVisibleDots.value) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const start = Math.max(0, currentCardIndex.value - 2);
  const end = Math.min(total, start + maxVisibleDots.value);
  return Array.from({ length: end - start }, (_, i) => start + i);
});

// Methods
const scrollToCard = (i) => {
  const cardsRow = bucketScroller.value?.querySelector(".bucket-cards-row");
  const cards = bucketCards.value || [];
  const cardEl = cards[i]?.$refs?.bucketCard;

  if (!cardEl || !cardsRow) return;

  const cardWidth = 240;
  const gap = 12;
  const containerWidth = cardsRow.clientWidth;
  const scrollPosition = (cardWidth + gap) * i - (containerWidth - cardWidth) / 2;

  cardsRow.scrollTo({
    left: Math.max(0, scrollPosition),
    behavior: "smooth",
  });

  currentCardIndex.value = i;
};

const previousCard = () => {
  if (!canPrev.value) return;
  const cardsRow = bucketScroller.value?.querySelector(".bucket-cards-row");
  if (!cardsRow) return;

  const cardWidth = 240;
  const gap = 12;
  const containerWidth = cardsRow.clientWidth;
  const cardsPerView = Math.floor(containerWidth / (cardWidth + gap));
  const newIndex = Math.max(0, (currentCardIndex.value ?? 0) - cardsPerView);
  scrollToCard(newIndex);
};

const nextCard = () => {
  if (!canNext.value) return;
  const cardsRow = bucketScroller.value?.querySelector(".bucket-cards-row");
  if (!cardsRow) return;

  const cardWidth = 240;
  const gap = 12;
  const containerWidth = cardsRow.clientWidth;
  const cardsPerView = Math.floor(containerWidth / (cardWidth + gap));
  const total = bucketStats.value?.length || 0;
  const newIndex = Math.min(total - 1, (currentCardIndex.value ?? 0) + cardsPerView);
  scrollToCard(newIndex);
};

const handleScroll = () => {
  const cardsRow = bucketScroller.value?.querySelector(".bucket-cards-row");
  if (!cardsRow) return;

  const cardWidth = 240;
  const gap = 12;
  const scrollLeft = cardsRow.scrollLeft;
  const newIndex = Math.round(scrollLeft / (cardWidth + gap));

  if (newIndex !== currentCardIndex.value && newIndex >= 0 && newIndex < bucketStats.value.length) {
    currentCardIndex.value = newIndex;
  }
};

const setupScrollListener = () => {
  const cardsRow = bucketScroller.value?.querySelector(".bucket-cards-row");
  if (cardsRow) {
    cardsRow.addEventListener("scroll", handleScroll);
  }
};

const removeScrollListener = () => {
  const cardsRow = bucketScroller.value?.querySelector(".bucket-cards-row");
  if (cardsRow) {
    cardsRow.removeEventListener("scroll", handleScroll);
  }
};

const selectOwner = (user) => {
  selectedOwner.value = user;
  emit("owner-selected", { user, type: "bucket" });
};

// Initialize selected owner
const initializeSelectedOwner = () => {
  if (users.value && users.value.length > 0) {
    const orgOwner = users.value.find((u) => u.role === "owner");
    selectedOwner.value = orgOwner || account.value;

    if (route.query.bucketOwner && route.query.bucketOwner !== "All") {
      nextTick(() => {
        const user = users.value.find((u) => u.id === route.query.bucketOwner);
        if (user) {
          selectedOwner.value = user;
        }
      });
    }
  } else {
    selectedOwner.value = account.value;
  }
};

// Watch route query for bucketOwner
watch(
  () => route.query.bucketOwner,
  (newOwner) => {
    if (newOwner && newOwner !== "All") {
      const user = users.value?.find((u) => u.id === newOwner);
      if (user) {
        selectedOwner.value = user;
      }
    } else {
      const orgOwner = users.value?.find((u) => u.role === "owner");
      selectedOwner.value = orgOwner || account.value;
    }
  }
);

// Watch users data
watch(
  users,
  () => {
    if (users.value && users.value.length > 0) {
      if (route.query.bucketOwner && route.query.bucketOwner !== "All") {
        const user = users.value.find((u) => u.id === route.query.bucketOwner);
        if (user) {
          selectedOwner.value = user;
        }
      }
    }
  },
  { immediate: true }
);

// Watch bucketStats to reset scroll position
watch(bucketStats, () => {
  nextTick(() => {
    currentCardIndex.value = 0;
    setupScrollListener();
  });
});

// Lifecycle
onMounted(() => {
  setupScrollListener();
  initializeSelectedOwner();
});

onBeforeUnmount(() => {
  removeScrollListener();
});
</script>

<style scoped>
.reports-card {
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
}

.card-body {
  padding: 16px 20px 12px 20px;
  background: transparent;
}

.bucket-header {
  background: transparent;
  margin: 0 0 12px 0;
  align-items: center;
}

.bucket-legend {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 12px;
  padding: 0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.due-today-dot {
  background-color: #059669;
}

.overdue-dot {
  background-color: #dc2626;
}

.upcoming-dot {
  background-color: #d97706;
}

.legend-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.bucket-title {
  font-weight: 600;
  font-size: 1.25rem;
  color: #111827;
  margin: 0;
}

.new-bucket-btn {
  gap: 6px;
}

.new-bucket-btn :deep(.ui-button__icon) {
  font-size: 14px;
  width: 14px;
  height: 14px;
  margin-right: 0;
}

.bucket-scroll-container {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  display: block;
  background: transparent;
  padding-bottom: 0;
}

.bucket-cards-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  overflow-x: auto;
  scroll-behavior: smooth;
  position: relative;
  z-index: 1;
  padding-bottom: 0;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.bucket-cards-row::-webkit-scrollbar {
  height: 6px;
}

.bucket-cards-row::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.bucket-cards-row::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
  transition: background 0.2s ease;
}

.bucket-cards-row::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Pagination */
.bucket-pagination-wrapper {
  padding: 8px 0 0 0;
  background: transparent;
  margin-top: 4px;
}

.bucket-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.bucket-nav-btn {
  border: none;
  outline: none;
  background: #f9fafb;
  color: #6b7280;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  width: 36px;
  height: 36px;
}

.bucket-nav-btn:hover:not(:disabled) {
  background: #f3f4f6;
  color: #3e3efe;
  transform: scale(1.05);
}

.bucket-nav-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.bucket-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f9fafb;
}

.bucket-dots-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bucket-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  max-width: 300px;
}

.bucket-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d1d5db;
  cursor: pointer;
  border: none;
  padding: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex-shrink: 0;
}

.bucket-dot:hover {
  background: #9ca3af;
  transform: scale(1.2);
}

.bucket-dot.active {
  background: #3e3efe;
  width: 24px;
  border-radius: 12px;
  transform: scale(1);
}

.bucket-dot.dot-before-active,
.bucket-dot.dot-after-active {
  background: #9ca3af;
  width: 10px;
  height: 10px;
}

.dots-indicator {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  padding: 4px 8px;
  background: #f9fafb;
  border-radius: 6px;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .bucket-dots {
    max-width: 200px;
    gap: 4px;
  }

  .bucket-dot {
    width: 6px;
    height: 6px;
  }

  .bucket-dot.active {
    width: 20px;
  }

  .bucket-nav-btn {
    width: 32px;
    height: 32px;
    padding: 6px;
  }
}

.bucket-loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  min-height: 200px;
}
</style>
